import React, { useContext, useMemo } from 'react';
import { EchartsWithTheme, Empty, Modal, useTheme } from '@maxtropy/components';
import { Spin } from 'antd';
import styles from './index.module.scss';
import outerStyles from '../index.module.scss';
import dayjs from 'dayjs';
import { GasMonitorContext } from '../../../context';
import { ShareDataWrapperContext } from '@/pages/WgqAllMaps';
import { IndexTimeActive } from '../../LeftBox/SwitchDate';
import { isNil } from 'lodash-es';
import { normalizeChartUnit } from '@/pages/WgqAllMaps/utils';

interface CostTrendModalProps {
  open: boolean;
  onClose: () => void;
}

const CostTrendModal: React.FC<CostTrendModalProps> = ({ open, onClose }) => {
  const { isFullScreen, scale: pageScale } = useContext(ShareDataWrapperContext);
  const { gasCostTrendData: data, gasCostTrendLoading: loading, timeResolution } = useContext(GasMonitorContext);

  const timeFormat = timeResolution === IndexTimeActive.THIS_MONTH ? 'MM-DD' : 'M月';

  const theme = useTheme();

  const trendDetails = useMemo(() => {
    return data?.gasCostTrendDetails ?? [];
  }, [data]);

  const { unit, data: normalizedData } = useMemo(() => {
    return normalizeChartUnit(trendDetails, {
      valueKey: 'cost',
      baseUnit: '元',
    });
  }, [trendDetails]);

  const averageCost = useMemo(() => {
    if (normalizedData.length === 0) return 0;
    const sum = normalizedData.reduce((acc, d) => acc + (d.cost ?? 0), 0);
    return sum / normalizedData.filter(item => !isNil(item.cost)).length;
  }, [normalizedData]);

  const chartOption = useMemo(() => {
    return {
      backgroundColor: 'transparent',
      color: ['rgb(22, 221, 142)'],
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const point = params[0];
          return `${point.name}<br/>成本: ${point.value?.toFixed(1) ?? '--'}${unit}`;
        },
      },
      xAxis: {
        showMinLabel: true,
        showMaxLabel: true,
        hideOverlap: true,
        data: normalizedData.map(d => dayjs(d.time, 'x').format(timeFormat)),
      },
      yAxis: {
        type: 'value',
        name: unit,
      },
      grid: {
        left: 10,
        right: 10,
        top: 60,
        bottom: 10,
        containLabel: true,
      },
      legend: {
        top: 0,
        right: 10,
        icon: 'rect',
        itemWidth: 12,
        itemHeight: 12,
      },
      series: [
        {
          type: 'line',
          smooth: true,
          name: '总成本',
          showSymbol: false,
          emphasis: {
            focus: 'series',
          },
          data: normalizedData.map(d => d.cost),
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(22, 221, 142, 0.20)' },
                { offset: 1, color: 'rgba(22, 221, 142, 0)' },
              ],
            },
          },
          markLine: {
            silent: true,
            symbol: ['none', 'none'],
            data: [{ yAxis: averageCost, name: '平均值' }],
            lineStyle: { color: '#faad14', type: 'dashed' },
            label: {
              formatter: `平均值: ${averageCost.toFixed(1)}${unit}`,
              position: 'insideEndTop',
              color: '#faad14',
            },
          },
        },
      ],
    };
  }, [normalizedData, averageCost, timeFormat, unit]);

  return (
    <Modal
      title="成本趋势"
      open={open}
      onCancel={onClose}
      footer={null}
      size="big"
      destroyOnClose
      centered
      style={{ top: isFullScreen ? 80 * (pageScale ?? 1) : 110 * (pageScale ?? 1) }}
      className={outerStyles.customModal}
      getContainer={() => document.getElementById('full_screen_box') ?? document.body}
    >
      <Spin spinning={loading}>
        <div className={styles.modalContent}>
          <div className={styles.chartWrapper}>
            <EchartsWithTheme
              empty={{
                isShowEmpty: !normalizedData || normalizedData.length === 0,
                emptyNode: <Empty style={{ marginTop: 200 }} />,
              }}
              loading={loading}
              option={chartOption}
              theme={theme}
              style={{ height: 620 }}
            />
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default CostTrendModal;
